/**
 * Eid party invitations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidPartyInvitationsPack: ToilePack = {
    slug: 'eid-party-invitations',
    category: 'season',
    name: 'Eid party invitations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dinner', label: 'Eid dinner', subject: 'a vertical invitation headed "Eid Dinner" with a long table of dishes drawn small above, and a large clear empty panel below for the details'},
        {key: 'openhouse', label: 'Open house', subject: 'a vertical invitation headed "Eid Open House" with an open doorway framed by lanterns and ruled blank lines for date, time and address'},
        {key: 'kids', label: 'Kids party', subject: 'a vertical invitation headed "Eid Party" with balloons, a crescent and confetti, and a blank band for the child\'s name and the time'},
    ],
    styles: [
        {key: 'foil', label: 'Gold foil', style: 'metallic gold foil linework with fine ornament and a thin double border', palette: 'gold on midnight blue', framing: 'Centered on the card with an even margin around the border'},
        {key: 'papercut', label: 'Paper cut', style: 'flat layered paper-cut shapes in overlapping tones with clean edges', palette: 'teal, plum and gold', framing: 'Centered on the card with an even margin around the artwork'},
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolor illustration with soft bleeding edges and a hand-painted border', palette: 'indigo, blush and gold washes on textured white paper', framing: 'Full card with a painted border, any message area left as clean paper'},
    ],
    directions: ['BBQ party', 'garden party', 'henna night'],
    directionHint: 'try: BBQ party',
    seo: {
        title: 'Eid party invitation: dinner, open house, kids party',
        metaDescription: 'Nine printable Eid party invitations headed Eid Dinner, Eid Open House or Eid Party, with a blank panel for the date, time and address.',
        h1: 'Eid party invitations: dinner, open house, kids party',
        tagline: 'Nine printable invitations, three designs each in gold foil, paper cut and watercolor.',
        intro: 'Printable invitation cards for a host inviting family and friends to an Eid lunch, a dinner or an open house. Each card has a lettered heading, Eid Dinner, Eid Open House or Eid Party, above a long table of dishes, an open doorway framed by lanterns, or balloons and confetti. A blank panel or ruled lines hold the date, time and address. Type a direction such as BBQ party or garden party and the card is drawn with it.',
        useCases: [
            'an Eid lunch with family',
            'an open house for neighbours',
            "a children's Eid party",
            'an Eid dinner for relatives',
        ],
        faq: [
            {q: 'Where do the details go?', a: 'In the blank panel, or on the ruled lines for the date, time and address.'},
            {q: 'Which invitation is for children?', a: "The third design, headed Eid Party, with balloons, a crescent and confetti, and a band for the child's name and the time."},
            {q: 'What are the three styles?', a: 'Gold foil is metallic linework with a thin double border, paper cut is flat layered shapes, and watercolor is a loose painted illustration.'},
            {q: 'Can I ask for a different event?', a: 'Yes. Type a direction such as henna night and the card is drawn with it. That is a request, not a promised result.'},
        ],
    },
}
